import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Product } from "@/entities/Product";
import { Creator } from "@/entities/Creator";
import { User } from "@/entities/User";
import { LayoutDashboard, Package, Users, UploadCloud, TrendingUp, Star, AlertCircle, PlusCircle, ArrowRight } from "lucide-react";

export default function Admin() {
  const [stats, setStats] = useState({ total: 0, active: 0, draft: 0, soldOut: 0, creators: 0, featured: 0 });
  const [recentProducts, setRecentProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [unauthorized, setUnauthorized] = useState(false);

  useEffect(() => {
    async function load() {
      const me = await User.me();
      setUser(me);
      if (me?.role !== "admin") { setUnauthorized(true); setLoading(false); return; }

      const [allProducts, creators, featured] = await Promise.all([
        Product.list("-createdAt", 100),
        Creator.list("-createdAt", 100),
        Product.filter({ is_featured: true }, "-createdAt", 6),
      ]);

      setStats({
        total: allProducts.length,
        active: allProducts.filter(p => p.status === "active").length,
        draft: allProducts.filter(p => p.status === "draft").length,
        soldOut: allProducts.filter(p => p.status === "sold_out").length,
        creators: creators.length,
        featured: allProducts.filter(p => p.is_featured).length,
      });
      setRecentProducts(allProducts.slice(0, 5));
      setLoading(false);
    }
    load();
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="animate-spin w-8 h-8 border-4 border-[#c8a96e] border-t-transparent rounded-full" />
    </div>
  );

  if (unauthorized) return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
      <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
      <h2 className="font-display text-2xl font-bold text-[#1a1a1a] mb-2">Admin Access Only</h2>
      <p className="text-gray-500 text-sm mb-6">You need admin privileges to access this area.</p>
      <Link to={createPageUrl("Home")} className="bg-[#1a1a1a] text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-[#c8a96e] transition-colors">
        Back to Store
      </Link>
    </div>
  );

  const statCards = [
    { label: "Total Products", value: stats.total, icon: <Package className="w-5 h-5" />, color: "bg-blue-50 text-blue-600" },
    { label: "Active Listings", value: stats.active, icon: <TrendingUp className="w-5 h-5" />, color: "bg-green-50 text-green-600" },
    { label: "Drafts", value: stats.draft, icon: <AlertCircle className="w-5 h-5" />, color: "bg-yellow-50 text-yellow-600" },
    { label: "Sold Out", value: stats.soldOut, icon: <Package className="w-5 h-5" />, color: "bg-red-50 text-red-500" },
    { label: "Creators", value: stats.creators, icon: <Users className="w-5 h-5" />, color: "bg-purple-50 text-purple-600" },
    { label: "Highly Rated", value: stats.featured, icon: <Star className="w-5 h-5" />, color: "bg-amber-50 text-amber-600" },
  ];

  const quickActions = [
    { title: "Manage Products", desc: "Add, edit, or remove product listings", icon: <Package className="w-6 h-6" />, page: "ProductManager", color: "border-blue-200 hover:border-blue-400" },
    { title: "Manage Creators", desc: "Update creator profiles and featured status", icon: <Users className="w-6 h-6" />, page: "CreatorManager", color: "border-purple-200 hover:border-purple-400" },
    { title: "Upload Inventory", desc: "Bulk upload in-house inventory via CSV or manual entry", icon: <UploadCloud className="w-6 h-6" />, page: "InventoryUpload", color: "border-green-200 hover:border-green-400" },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <LayoutDashboard className="w-5 h-5 text-[#c8a96e]" />
            <p className="text-[#c8a96e] text-xs font-semibold uppercase tracking-widest">Admin Panel</p>
          </div>
          <h1 className="font-display text-3xl font-bold text-[#1a1a1a]">Inventory Dashboard</h1>
          <p className="text-gray-500 text-sm mt-1">Welcome back, {user?.fullName || "Admin"}</p>
        </div>
        <Link
          to={createPageUrl("ProductManager")}
          className="hidden sm:inline-flex items-center gap-2 bg-[#1a1a1a] hover:bg-[#c8a96e] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors"
        >
          <PlusCircle className="w-4 h-4" /> Add Product
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-10">
        {statCards.map((s) => (
          <div key={s.label} className="bg-white border border-[#e8e4de] rounded-xl p-4">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${s.color}`}>
              {s.icon}
            </div>
            <div className="font-display text-2xl font-bold text-[#1a1a1a]">{s.value}</div>
            <div className="text-xs text-gray-500 mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-10">
        {quickActions.map((a) => (
          <Link
            key={a.page}
            to={createPageUrl(a.page)}
            className={`group bg-white border-2 rounded-xl p-6 transition-all hover:shadow-md ${a.color}`}
          >
            <div className="w-12 h-12 bg-[#faf9f7] rounded-xl flex items-center justify-center mb-4 text-[#c8a96e] group-hover:bg-[#c8a96e] group-hover:text-white transition-colors">
              {a.icon}
            </div>
            <h3 className="font-semibold text-[#1a1a1a] mb-1">{a.title}</h3>
            <p className="text-xs text-gray-500 leading-relaxed mb-3">{a.desc}</p>
            <span className="inline-flex items-center gap-1 text-xs font-medium text-[#c8a96e]">
              Open <ArrowRight className="w-3 h-3" />
            </span>
          </Link>
        ))}
      </div>

      {/* Recent Products */}
      <div className="bg-white border border-[#e8e4de] rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#e8e4de]">
          <h2 className="font-semibold text-[#1a1a1a]">Recent Products</h2>
          <Link to={createPageUrl("ProductManager")} className="text-xs text-[#c8a96e] font-medium hover:underline">
            View All
          </Link>
        </div>
        <div className="divide-y divide-[#e8e4de]">
          {recentProducts.map((p) => (
            <div key={p.id} className="flex items-center gap-4 px-6 py-3">
              <div className="w-10 h-10 rounded-lg overflow-hidden bg-[#f5f3ef] flex-shrink-0">
                <img src={p.images?.[0]} alt={p.name} className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#1a1a1a] truncate">{p.name}</p>
                <p className="text-xs text-gray-400">{p.creator_name} · {p.category}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-sm font-semibold text-[#1a1a1a]">${p.price}</p>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                  p.status === "active" ? "bg-green-100 text-green-600" :
                  p.status === "draft" ? "bg-yellow-100 text-yellow-600" :
                  "bg-red-100 text-red-500"
                }`}>
                  {p.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}